import { create } from "zustand";
import { applyThemeToDocument } from "../lib/theme-dom";
import {
  detectInitialThemePreference,
  getSystemThemeMediaQuery,
  persistThemePreference,
  resolveEffectiveTheme,
  resolveSystemTheme,
  resolveThemePreference,
  type ResolvedShellTheme,
  type ShellThemePreference,
} from "../lib/theme-preferences";

type MediaQueryListWithLegacy = MediaQueryList & {
  addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
};

interface ShellThemeState {
  preference: ShellThemePreference;
  resolvedTheme: ResolvedShellTheme;
  initialize: () => void;
  setPreference: (preference: ShellThemePreference) => void;
  syncWithSystem: (resolvedTheme: ResolvedShellTheme) => void;
}

const initialPreference = detectInitialThemePreference();

let hasInitializedThemeStore = false;
let cleanupSystemThemeListener: (() => void) | null = null;

const resolveMediaQueryList = (): MediaQueryListWithLegacy | null => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }

  return window.matchMedia(getSystemThemeMediaQuery());
};

const stopSystemThemeListener = () => {
  cleanupSystemThemeListener?.();
  cleanupSystemThemeListener = null;
};

const updateSystemThemeListener = (preference: ShellThemePreference) => {
  stopSystemThemeListener();

  if (preference !== "system") {
    return;
  }

  const mediaQueryList = resolveMediaQueryList();

  if (!mediaQueryList) {
    return;
  }

  const handleChange = (event: MediaQueryListEvent) => {
    useShellThemeStore
      .getState()
      .syncWithSystem(event.matches ? "dark" : "light");
  };

  if (typeof mediaQueryList.addEventListener === "function") {
    mediaQueryList.addEventListener("change", handleChange);
    cleanupSystemThemeListener = () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
    return;
  }

  if (typeof mediaQueryList.addListener === "function") {
    mediaQueryList.addListener(handleChange);
    cleanupSystemThemeListener = () => {
      mediaQueryList.removeListener?.(handleChange);
    };
  }
};

const applyThemeState = (
  preference: ShellThemePreference,
  resolvedTheme: ResolvedShellTheme,
) => {
  applyThemeToDocument(preference, resolvedTheme);
};

export const useShellThemeStore = create<ShellThemeState>((set, get) => ({
  preference: initialPreference,
  resolvedTheme: resolveEffectiveTheme(initialPreference, resolveSystemTheme()),
  initialize: () => {
    const preference = detectInitialThemePreference();
    const resolvedTheme = resolveEffectiveTheme(
      preference,
      resolveSystemTheme(),
    );

    set({
      preference,
      resolvedTheme,
    });
    applyThemeState(preference, resolvedTheme);
    updateSystemThemeListener(preference);
  },
  setPreference: (preference) => {
    const nextPreference = resolveThemePreference(preference);

    if (!nextPreference) {
      return;
    }

    const resolvedTheme = resolveEffectiveTheme(
      nextPreference,
      resolveSystemTheme(),
    );

    persistThemePreference(nextPreference);
    set({
      preference: nextPreference,
      resolvedTheme,
    });
    applyThemeState(nextPreference, resolvedTheme);
    updateSystemThemeListener(nextPreference);
  },
  syncWithSystem: (resolvedTheme) => {
    if (get().preference !== "system") {
      return;
    }

    set({ resolvedTheme });
    applyThemeState("system", resolvedTheme);
  },
}));

export const initializeShellThemeStore = () => {
  if (hasInitializedThemeStore) {
    return;
  }

  hasInitializedThemeStore = true;
  useShellThemeStore.getState().initialize();
};
